import { supabase } from '@/integrations/supabase/client';
import { pipeline } from '@huggingface/transformers';

let embeddingPipeline: any = null;
const EMBEDDING_PRIMARY_CHAR_LIMIT = 12000;
const EMBEDDING_RETRY_CHAR_LIMIT = 6000;

async function getEmbeddingPipeline() {
  if (embeddingPipeline) return embeddingPipeline;
  embeddingPipeline = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
  return embeddingPipeline;
}

function ensureFlatArray(arr: any): number[] | null {
  if (!Array.isArray(arr)) return null;
  return Array.isArray(arr[0]) ? arr[0] : arr;
}

function normalizeTextForEmbedding(text: string, maxLength: number) {
  return text.replace(/\s+/g, ' ').trim().slice(0, maxLength);
}

async function generateEmbeddingMiniLM384(text: string): Promise<number[] | null> {
  try {
    if (!text?.trim()) return null;
    const pipe = await getEmbeddingPipeline();
    const output = await pipe(normalizeTextForEmbedding(text, EMBEDDING_PRIMARY_CHAR_LIMIT), {
      pooling: 'mean',
      normalize: true,
    });

    if (output && typeof output === 'object') {
      if (typeof output.data === 'function') {
        const data = await output.data();
        return ensureFlatArray(data);
      }
      if (typeof output.tolist === 'function') {
        return ensureFlatArray(output.tolist());
      }
      if (output.ort_tensor?.data) {
        return ensureFlatArray(output.ort_tensor.data);
      }
    }

    return ensureFlatArray(output);
  } catch (error) {
    console.error('[Embeddings] MiniLM embedding generation failed:', error);
    return null;
  }
}

export async function generateEmbedding384(text: string): Promise<number[] | null> {
  const embedding = await generateEmbeddingMiniLM384(text);
  return embedding?.length === 384 ? embedding : null;
}

async function request1536Embedding(input: string) {
  const { data, error } = await supabase.functions.invoke('openai-proxy', {
    body: {
      endpoint: 'embeddings',
      body: {
        model: 'text-embedding-3-small',
        input,
      },
    },
  });

  if (error) throw error;

  const embedding = data?.data?.[0]?.embedding;
  if (!Array.isArray(embedding) || embedding.length !== 1536) {
    console.warn('[Embeddings] Unexpected 1536-d embedding payload:', data);
    return null;
  }

  return embedding as number[];
}

export async function generateEmbedding1536(text: string): Promise<number[] | null> {
  try {
    if (!text?.trim()) return null;

    const primaryInput = normalizeTextForEmbedding(text, EMBEDDING_PRIMARY_CHAR_LIMIT);
    try {
      return await request1536Embedding(primaryInput);
    } catch (primaryError) {
      const retryInput = normalizeTextForEmbedding(text, EMBEDDING_RETRY_CHAR_LIMIT);
      if (!retryInput || retryInput === primaryInput) {
        throw primaryError;
      }

      console.warn('[Embeddings] Retrying 1536-d embedding with shorter normalized input.');
      return await request1536Embedding(retryInput);
    }
  } catch (error) {
    console.warn('[Embeddings] 1536-d embedding unavailable, continuing without it.', error);
    return null;
  }
}

export async function generateEmbedding(text: string): Promise<number[] | null> {
  return generateEmbedding384(text);
}

export async function expandQueryWithLLM(query: string): Promise<string[]> {
  return [query];
}

export async function checkKnowledgeBaseEmbeddings(knowledgeBaseId: string): Promise<{
  total: number;
  withEmbeddings: number;
  embeddingDimensions: number | null;
}> {
  try {
    const { data, error } = await supabase
      .from('knowledge_base_documents')
      .select('embedding')
      .eq('knowledge_base_id', knowledgeBaseId);

    if (error) throw error;

    const documents = ((data ?? []) as Array<{ embedding?: unknown }>);
    const total = documents.length;
    const firstEmbedding = documents.find((doc) => Array.isArray(doc.embedding))?.embedding ?? null;
    const withEmbeddings = documents.filter((doc) => Array.isArray(doc.embedding)).length;

    return {
      total,
      withEmbeddings,
      embeddingDimensions: Array.isArray(firstEmbedding) ? firstEmbedding.length : null,
    };
  } catch (error) {
    console.error('Error checking knowledge base embeddings:', error);
    return {
      total: 0,
      withEmbeddings: 0,
      embeddingDimensions: null,
    };
  }
}